import React from 'react';

import { IAnalyticsConnectionMetadata } from '~src/settings/types/ISiteSettingsForSiteAdmin';

import { FlowAnalyticsExtensionConnection } from '../serverApi/types/FlowAnalyticsExtensionConnection';
import { AnalyticsExtensionConnectionPickerProps } from './AnalyticsExtensionConnectionPicker';

interface FlowAnalyticsExtensionEditorOptions {
  readonly initialConnection?: FlowAnalyticsExtensionConnection;
  readonly load: () => Promise<{
    options: ReadonlyArray<IAnalyticsConnectionMetadata>;
    connection: FlowAnalyticsExtensionConnection | null;
  }>;
  readonly applyConnection: (connectionLuid: string) => Promise<void>;
  readonly onApplied?: () => void;
}

// Shared by the connection dialog and the post-publish section. Both wait for
// the backend to persist the document and mapping before reporting success.
export function useFlowAnalyticsExtensionEditor({ initialConnection, load, applyConnection, onApplied }: FlowAnalyticsExtensionEditorOptions) {
  const [connection, setConnection] = React.useState(initialConnection);
  const [draftLuid, setDraftLuid] = React.useState(initialConnection?.connectionLuid ?? null);
  const [options, setOptions] = React.useState<ReadonlyArray<IAnalyticsConnectionMetadata>>([]);
  const [loaded, setLoaded] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [saveFailed, setSaveFailed] = React.useState(false);
  const mounted = React.useRef(true);
  const savingRef = React.useRef(false);
  React.useEffect(() => () => { mounted.current = false; }, []);

  const loadConnections = React.useCallback(async () => {
    const result = await load();
    if (mounted.current) {
      setOptions(result.options);
      setConnection(result.connection);
      setDraftLuid(result.connection?.connectionLuid ?? null);
      setLoaded(true);
    }
    return result.options;
  }, [load]);

  const selectedConnection = options.find(option => option.connection_luid === draftLuid && option.connection_brief.connection_type === 'TABPY');
  const canUpdate = loaded && connection?.canUpdate !== false;
  const canApply = canUpdate && !!selectedConnection && draftLuid !== connection?.connectionLuid;
  const apply = async () => {
    if (savingRef.current || !canApply) { return; }
    savingRef.current = true;
    setSaving(true);
    setSaveFailed(false);
    try {
      await applyConnection(draftLuid);
      if (mounted.current) {
        setConnection({
          connectionLuid: selectedConnection.connection_luid,
          connectionName: selectedConnection.connection_brief.connection_name,
          connectionType: selectedConnection.connection_brief.connection_type,
          canUpdate: true,
        });
        onApplied?.();
      }
    } catch {
      if (mounted.current) { setSaveFailed(true); }
    } finally {
      savingRef.current = false;
      if (mounted.current) { setSaving(false); }
    }
  };

  const pickerProps: AnalyticsExtensionConnectionPickerProps = {
    connectionLuid: draftLuid,
    currentConnection: connection ? {
      connection_luid: connection.connectionLuid,
      connection_brief: { connection_name: connection.connectionName, connection_type: connection.connectionType },
    } : null,
    selectConnection: setDraftLuid,
    loadConnections,
    disabled: saving || !canUpdate,
  };

  return { pickerProps, loaded, connection, saving, saveFailed, canApply, apply, isSaving: () => savingRef.current };
}
